import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React, { useCallback } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { Map } from 'immutable';
import { ContentBlock, ContentState, EditorState, genKey, Modifier, RichUtils, SelectionState } from 'draft-js';
import _forEach from 'lodash/forEach';
import _fill from 'lodash/fill';
import _map from 'lodash/map';
import { EMPTY_ARRAY } from '@tekion/tekion-base/app.constants';
import InlineStyleControl from './InlineStyleControl';
import BlockStyleControl from './BlockStyleControl';
import LinkControl from './LinkControl';
import Divider from '../divider/Divider';
import FontSizeControl from './FontSizeControl';
import TableControl from './TableControl';
import TextAlignControl from './TextAlignControl';
import LineHeightControl from './LineHeightControl';
import { addBlockDataForSelection } from '../helpers/rickTextEditor.helpers';
import { BLOCK_STYLE_PREFIX, BLOCK_STYLE_TYPES } from '../styles.contants';
import { TABLE_STYLE } from './TableControl/TableControl.constants';
import s from '../richTextEditor.module.scss';
var StyleControls = function StyleControls(_ref) {
  var editorState = _ref.editorState,
    onChange = _ref.onChange,
    controls = _ref.controls,
    customHandlers = _ref.customHandlers,
    styles = _ref.styles,
    extraControls = _ref.extraControls,
    className = _ref.className;
  var renderControl = function renderControl(control, index) {
    var key = "".concat(control.style).concat(control.type, ".").concat(index, ".key");
    switch (control.type) {
      case 'inline':
        return /*#__PURE__*/React.createElement(InlineStyleControl, {
          control: control,
          editorState: editorState,
          onToggle: toggleInlineStyle,
          key: key
        });
      case 'custom':
        return /*#__PURE__*/React.createElement(LinkControl, {
          control: control,
          editorState: editorState,
          onToggle: customHandlers[control.style],
          key: key
        });
      case 'divider':
        return /*#__PURE__*/React.createElement(Divider, {
          key: key
        });
      case 'dropdown':
        switch (control.style) {
          case 'fontSize':
            return /*#__PURE__*/React.createElement(FontSizeControl, {
              styles: styles,
              control: control,
              editorState: editorState,
              onToggle: changeFontSize,
              key: key
            });
          case 'textAlignment':
            return /*#__PURE__*/React.createElement(TextAlignControl, {
              styles: styles,
              control: control,
              editorState: editorState,
              onToggle: changeTextAlignment,
              key: key
            });
          case 'insertTable':
            return /*#__PURE__*/React.createElement(TableControl, {
              styles: styles,
              control: control,
              editorState: editorState,
              onToggle: insertTable,
              key: key
            });
          case 'changeLineHeight':
            return /*#__PURE__*/React.createElement(LineHeightControl, {
              styles: styles,
              control: control,
              editorState: editorState,
              onToggle: changeLineHeight,
              key: key
            });
          default:
            return /*#__PURE__*/React.createElement(BlockStyleControl, {
              control: control,
              editorState: editorState,
              onToggle: toggleBlockType,
              key: key
            });
        }
      default:
        return /*#__PURE__*/React.createElement(BlockStyleControl, {
          control: control,
          editorState: editorState,
          onToggle: toggleBlockType,
          key: key
        });
    }
  };
  var addDataToBlock = useCallback(function (blockKey, blockValue) {
    return addBlockDataForSelection(editorState)(blockKey, blockValue);
  }, [editorState]);
  var changeLineHeight = useCallback(function (lineHeight) {
    var newEditorState = addDataToBlock("".concat(BLOCK_STYLE_PREFIX).concat(BLOCK_STYLE_TYPES.LINE_HEIGHT), lineHeight);
    onChange(newEditorState);
  }, [onChange, addDataToBlock]);
  var changeTextAlignment = useCallback(function (alignment) {
    var newEditorState = addDataToBlock("".concat(BLOCK_STYLE_PREFIX).concat(BLOCK_STYLE_TYPES.TEXT_ALIGNMENT), alignment);
    onChange(newEditorState);
  }, [onChange, addDataToBlock]);
  var changeFontSize = useCallback(function (value) {
    var modifiedEditorState = styles.fontSize.add(styles.fontSize.remove(editorState), value);
    var validEditorState = EditorState.set(EditorState.createEmpty(), {
      currentContent: modifiedEditorState.getCurrentContent(),
      inlineStyleOverride: modifiedEditorState.getCurrentInlineStyle(),
      selection: modifiedEditorState.getSelection()
    });
    onChange(validEditorState);
  }, [editorState, onChange, styles]);
  var toggleBlockType = useCallback(function (blockType) {
    onChange(RichUtils.toggleBlockType(editorState, blockType));
  }, [editorState, onChange]);
  var toggleInlineStyle = useCallback(function (inlineStyle) {
    onChange(RichUtils.toggleInlineStyle(editorState, inlineStyle));
  }, [editorState, onChange]);
  var insertTable = function insertTable(tableProps) {
    var _blockArray$index, _blockArray;
    var size = tableProps.size;
    var selection = editorState.getSelection();
    if (!selection.isCollapsed()) {
      return null;
    }
    // Don't insert a table within a table
    if (editorState.getCurrentContent().getBlockForKey(selection.getAnchorKey()).getType() === 'table') {
      return null;
    }
    var defaultCellStyle = {
      border: '1px solid rgb(0, 0, 0, 0.25)',
      width: "".concat(100 / size.cols, "%"),
      padding: '6px',
      'text-align': 'left'
    };
    var cols = _fill(Array(size.cols), 1);
    var tableShape = _map(_fill(Array(size.rows), cols), function (row) {
      return _map(row, function () {
        return {
          element: 'td',
          style: _objectSpread({}, defaultCellStyle)
        };
      });
    });
    var tableKey = genKey();
    var newBlocks = [];
    _forEach(tableShape, function (row, rowIndex) {
      _forEach(row, function (cell, cellIndex) {
        var data = Map({
          tableKey: tableKey,
          tablePosition: "".concat(tableKey, "-").concat(rowIndex, "-").concat(cellIndex),
          'text-align': 'center'
        });
        if (rowIndex === 0 && cellIndex === 0) {
          data = data.set('tableShape', tableShape).set('tableStyle', _objectSpread({}, TABLE_STYLE)).set('rowStyle', []);
        }
        var newBlock = new ContentBlock({
          key: genKey(),
          type: 'table',
          text: '',
          data: data
        });
        newBlocks.push(newBlock);
      });
    });
    var selectionKey = selection.getAnchorKey();
    var contentState = editorState.getCurrentContent();
    contentState = Modifier.splitBlock(contentState, selection);
    var blockArray = contentState.getBlocksAsArray();
    var currBlock = contentState.getBlockForKey(selectionKey);
    var index = blockArray.findIndex(function (block) {
      return block === currBlock;
    });
    var isEnd = index === blockArray.length - 1;
    if (((_blockArray$index = blockArray[index]) === null || _blockArray$index === void 0 ? void 0 : _blockArray$index.getType()) === 'table') {
      newBlocks.unshift(new ContentBlock({
        key: genKey()
      }));
    }
    if (((_blockArray = blockArray[index + 1]) === null || _blockArray === void 0 ? void 0 : _blockArray.getType()) === 'table') {
      newBlocks.push(new ContentBlock({
        key: genKey()
      }));
    }
    blockArray.splice.apply(blockArray, [index + 1, 0].concat(newBlocks));
    if (isEnd) {
      blockArray.push(new ContentBlock({
        key: genKey()
      }));
    }
    var entityMap = contentState.getEntityMap();
    contentState = ContentState.createFromBlockArray(blockArray, entityMap);
    var newEditorState = EditorState.push(editorState, contentState, 'insert-fragment');
    var key = newBlocks[0].getKey();
    selection = SelectionState.createEmpty(key);
    newEditorState = EditorState.acceptSelection(newEditorState, selection);
    return onChange(newEditorState);
  };
  return /*#__PURE__*/React.createElement("div", {
    className: cx(className, s.RichEditorControls)
  }, /*#__PURE__*/React.createElement("div", {
    className: s.StyleControls
  }, controls.map(function (control, index) {
    return renderControl(control, index);
  })), /*#__PURE__*/React.createElement("div", {
    className: s.extraControls
  }, extraControls.map(function (extraControl) {
    return extraControl;
  })));
};
StyleControls.propTypes = {
  editorState: PropTypes.instanceOf(EditorState).isRequired,
  onChange: PropTypes.func.isRequired,
  controls: PropTypes.array.isRequired,
  customHandlers: PropTypes.object.isRequired,
  styles: PropTypes.object.isRequired,
  extraControls: PropTypes.array,
  className: PropTypes.string
};
StyleControls.defaultProps = {
  extraControls: EMPTY_ARRAY,
  className: ''
};
export default StyleControls;